import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Image from "react-bootstrap/Image";
import logo from "../../assets/images/logo.png";
import "./navbar.css";

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const user = useSelector((state) => state.user);
  const [openMenu, setOpenMenu] = useState(null);
  const [lang, setLang] = useState(i18n.language || "en");
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on navigation change
  useEffect(() => setMobileOpen(false), [location.pathname]);

  // Lock body scroll when mobile panel is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.classList.add('is-open');
    } else {
      document.body.classList.remove('is-open');
    }
  }, [mobileOpen]);

  const toggleMenu = (menu) => {
    setOpenMenu(openMenu === menu ? null : menu);
  };

  const changeLang = (lng) => {
    setLang(lng);
    i18n.changeLanguage(lng);
    document.body.dir = lng === "ar" ? "rtl" : "ltr";
  };

  // Localized tagline fallback to ensure translation even if remote translations are missing
  const taglineByLang = {
    en: "Innovating the Future",
    fr: "Innover l'avenir",
    de: "Die Zukunft gestalten",
    ar: "نبتكر المستقبل",
  };
  const langShort = (i18n.language || "en").split('-')[0];
  const localTagline = taglineByLang[langShort] || taglineByLang.en;

  const profile = user?.profile;
  const avatarSrc = profile?.profileImage || profile?.logo || null;
  const displayName = (profile?.username || profile?.name || user?.user?.username || user?.user?.unique_name || "").toString();
  const initial = (displayName.trim()[0] || "U").toUpperCase();
  const [avatarError, setAvatarError] = useState(false);
  const showAvatarImage = !!avatarSrc && !avatarError;
  const pickColor = (str) => {
    const palette = [
      "#0ea5e9", // sky
      "#22c55e", // green
      "#ef4444", // red
      "#a855f7", // purple
      "#f59e0b", // amber
      "#14b8a6", // teal
      "#6366f1", // indigo
    ];
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = (hash << 5) - hash + str.charCodeAt(i);
    const idx = Math.abs(hash) % palette.length;
    return palette[idx];
  };
  const avatarBg = pickColor(displayName || initial);
  // Use structured menu with submenus for cleaner navigation
  const useNewNav = false;

  // Active states for dropdown parents
  const aboutActive = ["/about", "/partners", "/faqs"].some((p) => location.pathname.startsWith(p));
  const memberActive = ["/eligibility", "/how-to-apply", "/benefits"].some((p) => location.pathname.startsWith(p));

  return (
    <header className={`navbar ${mobileOpen ? "is-open" : ""}`}>
      {/* ===== LEFT SECTION (BRAND + NAV LINKS) ===== */}
      <div className="navbar-left">
        <div className="navbar-brand">
          <Link to="/" className="navbar-logo">
            <Image src={logo} alt={t("Navbar.LogoAlt", { defaultValue: "Emirates Made" })} />
          </Link>
          <div className="navbar-tagline" style={{ textAlign: "center" }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#111", lineHeight: 1.2 }}>
              {t("EmiratesMade.Intro.taglineStrong", { defaultValue: localTagline })}
            </div>
          </div>
        </div>
        {/* ===== DESKTOP NAV ===== */}
        {useNewNav && (
          <nav className="navbar-links">
            <Link to="/about">{t("Navbar.About", { defaultValue: "About" })}</Link>
            <Link to="/objectives">{t("Navbar.Objectives", { defaultValue: "Objectives" })}</Link>
            <Link to="/manufacturers">{t("Navbar.Manufacturers", { defaultValue: "Manufacturers" })}</Link>
            <Link to="/products">{t("Navbar.Products", { defaultValue: "Products" })}</Link>
            <Link to="/benefits">{t("Navbar.Benefits", { defaultValue: "Benefits" })}</Link>
            <Link to="/label">{t("Navbar.Label", { defaultValue: "Label" })}</Link>
            <Link to="/partners">{t("Navbar.Partners", { defaultValue: "Partners" })}</Link>
            <Link to="/news">{t("Navbar.News", { defaultValue: "News" })}</Link>
            <Link to="/contact">{t("Navbar.Contact", { defaultValue: "Contact" })}</Link>
          </nav>
        )}
        {!useNewNav && (
        <nav className="navbar-links">
          {/* About */}
          <div
            className={`dropdown ${openMenu === "about" ? "open" : ""} ${aboutActive ? "active" : ""}`}
            onMouseEnter={() => toggleMenu("about")}
            onMouseLeave={() => toggleMenu(null)}
          >
            <button className="dropdown-btn">
              {t("Navbar.About", { defaultValue: "About" })} <span className="arrow">â–¾</span>
            </button>
            <ul className="dropdown-menu">
              <li><NavLink to="/about" className={({isActive}) => isActive ? 'active' : ''}>{t("Navbar.AboutProgram")}</NavLink></li>
              <li><NavLink to="/partners" className={({isActive}) => isActive ? 'active' : ''}>{t("Navbar.OurPartners")}</NavLink></li>
              <li><NavLink to="/faqs" className={({isActive}) => isActive ? 'active' : ''}>{t("Navbar.FAQs")}</NavLink></li>
            </ul>
          </div>

          {/* Becoming a Member */}
          <div
            className={`dropdown ${openMenu === "member" ? "open" : ""} ${memberActive ? "active" : ""}`}
            onMouseEnter={() => toggleMenu("member")}
            onMouseLeave={() => toggleMenu(null)}
          >
            <button className="dropdown-btn">
              {t("Navbar.BecomingMember", { defaultValue: "Becoming a Member" })} <span className="arrow">â–¾</span>
            </button>
            <ul className="dropdown-menu">
              <li><NavLink to="/eligibility" className={({isActive}) => isActive ? 'active' : ''}>{t("Navbar.CheckEligibility")}</NavLink></li>
              <li><NavLink to="/how-to-apply" className={({isActive}) => isActive ? 'active' : ''}>{t("Navbar.HowToApply")}</NavLink></li>
              <li><NavLink to="/benefits" className={({isActive}) => isActive ? 'active' : ''}>{t("Navbar.MemberBenefits")}</NavLink></li>
            </ul>
          </div>

          <NavLink to="/program-events" className={({isActive}) => isActive ? 'active' : ''}>{t("Navbar.ProgramEvents", { defaultValue: "Program Events" })}</NavLink>
          <NavLink to="/knowledge-hub" className={({isActive}) => isActive ? 'active' : ''}>{t("Navbar.KnowledgeHub", { defaultValue: "Knowledge Hub" })}</NavLink>
          <NavLink to="/members-directory" className={({isActive}) => isActive ? 'active' : ''}>{t("Navbar.MembersDirectory", { defaultValue: "Members Directory" })}</NavLink>
        </nav>
        )}
      </div>

      {/* (no center brand; logo stays left, centered over tagline) */}

      {/* ===== RIGHT SECTION ===== */}
            <div className="navbar-right">
        {!user?.authenticated && (
          <>
            <Link to="/login" className="login-link">
              <i className="bi bi-person"></i>
              {t("Navbar.Login", { defaultValue: "Login" })}
            </Link>
            <Link to="/register" className="join-btn">
              {t("Navbar.JoinCommunity", { defaultValue: "Join the Community" })}
            </Link>
          </>
        )}
        {user?.authenticated && (
          <>
            <Link to="/companies" className="join-btn" style={{ background: "var(--uae-green)" }}>
              {t("Navbar.Companies", { defaultValue: "Companies" })}
            </Link>
            <Link to="/Profile" className="login-link" title={t("Navbar.Profile", { defaultValue: "Profile" })}>
              {showAvatarImage ? (
                <img
                  src={avatarSrc}
                  alt="avatar"
                  onError={() => setAvatarError(true)}
                  style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", border: "1px solid #e5e7eb" }}
                />
              ) : (
                <div
                  aria-hidden
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    color: "#fff",
                    background: avatarBg,
                    border: "1px solid #e5e7eb",
                    textTransform: "uppercase",
                    fontSize: 18,
                  }}
                >
                  {initial}
                </div>
              )}
            </Link>
          </>
        )}<div className="lang-toggle">
          <button
            onClick={() => changeLang("en")}
            className={lang === "en" ? "active" : ""}
          >
            EN
          </button>
          <button
            onClick={() => changeLang("ar")}
            className={lang === "ar" ? "active" : ""}
          >
            AR
          </button>
        </div>

        {/* ===== HAMBURGER ===== */}
        <button
          className="hamburger"
          aria-label="Menu"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      {/* ===== MOBILE PANEL ===== */}
      <div className="mobile-panel">
        {useNewNav && (
        <nav className="mobile-links">
          <Link to="/about">{t("Navbar.About", { defaultValue: "About" })}</Link>
          <Link to="/objectives">{t("Navbar.Objectives", { defaultValue: "Objectives" })}</Link>
          <Link to="/manufacturers">{t("Navbar.Manufacturers", { defaultValue: "Manufacturers" })}</Link>
          <Link to="/products">{t("Navbar.Products", { defaultValue: "Products" })}</Link>
          <Link to="/benefits">{t("Navbar.Benefits", { defaultValue: "Benefits" })}</Link>
          <Link to="/label">{t("Navbar.Label", { defaultValue: "Label" })}</Link>
          <Link to="/partners">{t("Navbar.Partners", { defaultValue: "Partners" })}</Link>
          <Link to="/news">{t("Navbar.News", { defaultValue: "News" })}</Link>
          <Link to="/contact">{t("Navbar.Contact", { defaultValue: "Contact" })}</Link>
        </nav>
        )}
        {!useNewNav && (
        <nav className="mobile-links">
          <details>
            <summary>{t("Navbar.About", { defaultValue: "About" })}</summary>
            <div className="mobile-sub">
              <Link to="/about">{t("Navbar.AboutProgram", { defaultValue: "About the Program" })}</Link>
              <Link to="/partners">{t("Navbar.OurPartners", { defaultValue: "Our Partners" })}</Link>
              <Link to="/faqs">{t("Navbar.FAQs", { defaultValue: "FAQs" })}</Link>
            </div>
          </details>

          <details>
            <summary>{t("Navbar.BecomingMember", { defaultValue: "Becoming a Member" })}</summary>
            <div className="mobile-sub">
              <Link to="/eligibility">{t("Navbar.CheckEligibility", { defaultValue: "Check Eligibility" })}</Link>
              <Link to="/how-to-apply">{t("Navbar.HowToApply", { defaultValue: "How to Apply" })}</Link>
              <Link to="/benefits">{t("Navbar.MemberBenefits", { defaultValue: "Member Benefits" })}</Link>
            </div>
          </details>

          <NavLink to="/program-events" className={({isActive}) => isActive ? 'active' : ''}>{t("Navbar.ProgramEvents", { defaultValue: "Program Events" })}</NavLink>
          <NavLink to="/knowledge-hub" className={({isActive}) => isActive ? 'active' : ''}>{t("Navbar.KnowledgeHub", { defaultValue: "Knowledge Hub" })}</NavLink>
          <NavLink to="/members-directory" className={({isActive}) => isActive ? 'active' : ''}>{t("Navbar.MembersDirectory", { defaultValue: "Members Directory" })}</NavLink>
        </nav>
        )}

        <div className="mobile-cta">
          <Link to="/login" className="login-link mobile-login">
            <i className="bi bi-person"></i>
            {t("Navbar.Login", { defaultValue: "Login" })}
          </Link>

          <Link to="/register" className="join-btn wide">
            {t("Navbar.JoinCommunity", { defaultValue: "Join the Community" })}
          </Link>

          <div className="lang-toggle">
            <button
              onClick={() => changeLang("en")}
              className={lang === "en" ? "active" : ""}
            >
              EN
            </button>
            <button
              onClick={() => changeLang("ar")}
              className={lang === "ar" ? "active" : ""}
            >
              AR
            </button>
          </div>
        </div>
      </div>

      {/* ===== BACKDROP OVERLAY ===== */}
      {mobileOpen && <div className="navbar-overlay" onClick={() => setMobileOpen(false)} />}
    </header>
  );
};

export default Navbar;









